import { assign, fromPromise, setup } from "xstate";

import { useEvidenceStore } from "@/stores/evidence-store";
import { useAnnotationStore } from "@/stores/annotation-store";

import { $can } from "@/services/UtilitiesService";
import { saveAnnotation } from "@/services/AnnotationService";

import type { Evidence } from "@/models/Evidence";

type CaseEvidenceMachineContext =
  | ({ action: "EDIT" } & EditEvidenceProps)
  | ({ action: "REMOVE" } & RemoveEvidenceProps)
  | ({ action: "ADD"; states?: { duplicate?: boolean } } & AddEvidenceProps)
  | ({ action: "ACCEPT" } & AcceptMarkProps)
  | ({ action: "CHANGE_THEME" } & ChangeThemeProps);

interface GeneralMachineProps {
  brand: string;
  evidence: Evidence;
  caseReference: string;
}

interface AddEvidenceProps extends GeneralMachineProps {}
interface EditEvidenceProps extends GeneralMachineProps {
  isNameTooLong: boolean;
}
interface RemoveEvidenceProps extends GeneralMachineProps {
  all: boolean;
}
interface AcceptMarkProps extends GeneralMachineProps {
  acceptAndAdd: boolean;
}
interface ChangeThemeProps extends GeneralMachineProps {
  theme: string;
}

type CaseEvidenceMachineEvents =
  | { type: "STOP_EDITING" }
  | { type: "SAVE_ANNOTATION"; name: string }
  | { type: "CONFIRMED_DUPLICATE" };

export const BOOKMARK_NAME_MAX_LENGTH = 100;

export const caseEvidenceMachine = setup({
  types: {
    context: {} as CaseEvidenceMachineContext,
    events: {} as CaseEvidenceMachineEvents,
    input: {} as CaseEvidenceMachineContext,
  },
  actors: {
    saveAnnotation: fromPromise<undefined, { name: string } & EditEvidenceProps>(async (ctx) => {
      const { evidence, name } = ctx.input;
      await useEvidenceStore().renameEvidence({
        annotation: name,
        item: evidence,
      });
    }),
    acceptMark: fromPromise<undefined, { acceptAndAdd: boolean } & GeneralMachineProps>(async (ctx) => {
      const { evidence, acceptAndAdd } = ctx.input;
      const mark = useAnnotationStore().annotations.find((i) => i.markId === evidence.value);

      if (mark == null || mark.source === "user") return;

      const evidenceThemes = mark.evidenceThemes ?? [];
      const et = evidenceThemes?.find((e) => e.evidenceTheme === evidence.theme);

      if (!et) {
        // Add with different status if bookmark is not created by AI
        evidenceThemes.push({
          evidenceTheme: evidence.theme,
          usage: "bookmark",
          status: "User Accepted New Theme",
          confidence: 0,
        });
      } else et.status = "User Accepted Candidate for that Theme";

      await saveAnnotation({ ...mark, evidenceThemes });

      if (acceptAndAdd) {
        const { status, displayName, ...rest } = evidence;
        await useEvidenceStore().addEvidence(rest);
      }
    }),
    rejectCandidate: fromPromise<undefined, RemoveEvidenceProps>(async (ctx) => {
      const { all, evidence } = ctx.input;
      const a = useAnnotationStore().annotations.find((i) => i.markId === evidence.value);

      if (!a || a.source === "user") return;

      await useAnnotationStore().reject({
        annotation: a,
        reject: all ? { all } : { themes: [evidence.theme] },
      });
    }),
    deleteEvidence: fromPromise<undefined, RemoveEvidenceProps>(async (ctx) => {
      const { all, brand, caseReference, evidence } = ctx.input;

      const entries = all
        ? useEvidenceStore()
            .evidence.filter((e) => e.value === evidence.value)
            .map((item) => ({ caseReference, brand, item }))
        : [{ caseReference, brand, item: evidence }];

      await Promise.all(entries.map((entry) => useEvidenceStore().deleteEvidence(entry.item)));
    }),
    addEvidence: fromPromise<undefined, AddEvidenceProps>(async (ctx) => {
      try {
        await useEvidenceStore().addEvidence(ctx.input.evidence);
      } catch (error) {
        const res = error as Response;
        if (res.status === 400 && res.statusText === "Duplicate") {
          throw "Duplicate";
        }
      }
    }),
  },
}).createMachine({
  id: "CaseEvidenceMachine",
  initial: "idle",
  context: ({ input }) => input,
  states: {
    /* Initial state that immediately transitions to the state for the desired action */
    idle: {
      always: [
        {
          guard: ({ context }) => context.action === "REMOVE",
          target: "removeEvidence",
        },
        {
          guard: ({ context }) => context.action === "EDIT",
          target: "editingEvidence",
        },
        {
          guard: ({ context }) => context.action === "ADD",
          target: "addEvidence",
        },
        {
          guard: ({ context }) => context.action === "ACCEPT",
          target: "acceptMark",
        },
        {
          guard: ({ context }) => context.action === "CHANGE_THEME",
          target: "changeTheme",
        },
        { target: "stop" },
      ],
    },
    /*
    For editing an already existing evidence
    - Waits for the `SAVE_ANNOTATION` event, and starts saving if the new `name` is valid. Otherwise ends the machine
    - While saving if the evidence is a candidate, the mark is accepted before continuing to save the annotation.
    - Otherwise goes straight to saving the annotation
    - At any point, if a `STOP_EDITING` event is received, the machine ends.
    */
    editingEvidence: {
      id: "editingEvidence",
      initial: "editing",
      on: { STOP_EDITING: { target: "#CaseEvidenceMachine.stop" } },
      states: {
        editing: {
          on: {
            SAVE_ANNOTATION: { target: "saving" },
          },
        },
        saving: {
          entry: assign(({ event }) =>
            event.type === "SAVE_ANNOTATION" ? { isNameTooLong: event.name.length > BOOKMARK_NAME_MAX_LENGTH } : {},
          ),
          initial: "shouldSave",
          states: {
            shouldSave: {
              always: [
                {
                  target: "#editingEvidence.error",
                  guard: ({ context }) => context.action !== "EDIT" || context.isNameTooLong,
                },
                {
                  target: "acceptMark",
                  guard: ({ context }) => context.action === "EDIT" && context.evidence?.status === "candidate",
                },
                { target: "saveAnnotation" },
              ],
            },
            acceptMark: {
              invoke: {
                src: "acceptMark",
                input: ({ context }) =>
                  context.action === "EDIT" ? { ...context, acceptAndAdd: true } : ({} as never),
                onDone: { target: "saveAnnotation" },
              },
            },
            saveAnnotation: {
              invoke: {
                src: "saveAnnotation",
                input: ({ context, event }) =>
                  context.action === "EDIT"
                    ? {
                        name: event.type === "SAVE_ANNOTATION" ? event.name.trim() : "",
                        ...context,
                      }
                    : ({} as never),
                onError: { target: "#editingEvidence.error" },
                onDone: { target: "#editingEvidence.saved" },
              },
            },
          },
        },
        saved: {},
        error: {},
      },
    },
    /* 
    To delete an evidence,
    - Verifies the user is granted the bookmark permission, otherwise ends the machine
    - Rejects the candidates and/or multiple evidences depending on the `all` input
    */
    removeEvidence: {
      initial: "canRemove",
      states: {
        canRemove: {
          always: [{ target: "removing", guard: () => $can("bookmark") }, { target: "#CaseEvidenceMachine.stop" }],
        },
        removing: {
          type: "parallel",
          states: {
            rejectCandidate: {
              initial: "running",
              states: {
                running: {
                  invoke: {
                    src: "rejectCandidate",
                    onDone: { target: "done" },
                    input: ({ context }) =>
                      context.action === "REMOVE" ? { ...context, all: context.all } : ({} as never),
                  },
                },
                done: { type: "final" },
              },
            },
            deleteEvidences: {
              initial: "running",
              states: {
                running: {
                  invoke: {
                    src: "deleteEvidence",
                    input: ({ context }) =>
                      context.action === "REMOVE" ? { ...context, all: context.all } : ({} as never),
                  },
                },
                done: { type: "final" },
              },
            },
          },
          onDone: { target: "removed" },
        },
        removed: {},
        error: {},
      },
    },
    /*
    To add a new evidence
    - Verifies the user is granted the bookmark permission, otherwise ends the machine
    - Adds the evidence
    - Then, accepts the candidate mark
    - If a duplicate error occurs, the machine is held in an error state until the `CONFIRMED_DUPLICATE` event
      is sent, alerting the machine can end.
     */
    addEvidence: {
      initial: "canAdd",
      states: {
        canAdd: {
          always: [{ target: "adding", guard: () => $can("bookmark") }, { target: "#CaseEvidenceMachine.stop" }],
        },
        adding: {
          invoke: {
            src: "addEvidence",
            input: ({ context }) => (context.action === "ADD" ? context : (undefined as never)),
            onDone: { target: "acceptMark" },
            onError: {
              target: "error",
              actions: ({ context, event }) =>
                context.action === "ADD" ? { states: { duplicate: event.error === "Duplicate" } } : {},
            },
          },
        },
        acceptMark: {
          invoke: {
            src: "acceptMark",
            input: ({ context }) => ({ ...context, acceptAndAdd: false }),
            onDone: { target: "finished" },
          },
        },
        error: { on: { CONFIRMED_DUPLICATE: { target: "finished" } } },
        finished: { type: "final" },
      },
    },
    /* For primarily accepting a mark */
    acceptMark: {
      invoke: {
        src: "acceptMark",
        input: ({ context }) => (context.action === "ACCEPT" ? context : ({} as never)),
        onDone: { target: "stop" },
      },
    },
    /* 
    Changes the evidence's selected theme
    - Deletes the evidence
    - Re-adds it with the new evidence
    - Accepts the evidence
    */
    changeTheme: {
      initial: "deletingEvidence",
      states: {
        deletingEvidence: {
          invoke: {
            src: "deleteEvidence",
            input: ({ context }) => ({ ...context, all: false }),
            onDone: { target: "addEvidence" },
          },
        },
        addEvidence: {
          entry: assign(({ context }) =>
            context.action === "CHANGE_THEME"
              ? { evidence: { ...context.evidence, status: undefined, displayName: undefined, theme: context.theme } }
              : ({} as never),
          ),
          invoke: {
            src: "addEvidence",
            input: ({ context }) => context,
            onDone: { target: "acceptMark" },
          },
        },
        acceptMark: {
          invoke: {
            src: "acceptMark",
            input: ({ context }) => ({ ...context, acceptAndAdd: false }),
            onDone: { target: "#CaseEvidenceMachine.stop" },
          },
        },
      },
    },
    stop: { type: "final" },
  },
});
